// Typed storage keys - single source of truth
export const STORAGE_KEYS = {
  // Auth
  AUTH_TOKEN: 'quizball_auth_token',
  REFRESH_TOKEN: 'quizball_refresh_token',

  // User
  LOCALE: 'quizball_locale',
  ONBOARDING_COMPLETE: 'quizball_onboarding_complete',
  // Auth method (google/facebook/email/phone) the user last started auth with.
  // Carried from the auth tap to onboarding_completed so that event is method-aware.
  SIGNUP_METHOD: 'quizball_signup_method',
  WALKTHROUGH_COMPLETE: 'quizball_walkthrough_complete',
  PLAYER_STATE: 'quizball_player_state',
  USER_PREFERENCES: 'quizball_user_preferences',
  STORE_WALLET: 'quizball_store_wallet',
  POST_AUTH_REDIRECT: 'quizball_post_auth_redirect',
  MOBILE_VERIFICATION_REMINDER_DISMISSALS: 'quizball_mobile_verification_reminder_dismissals',

  // Game state
  DAILY_CHALLENGE_STATE: 'quizball_daily_challenge',
  // Frontend-only daily lock for the Guess-the-Card daily (backend has no type
  // for it yet); keyed by UTC day so it resets with the other dailies.
  WHEEL_SPIN_TIMESTAMP: 'quizball_wheel_spin',

  TRAINING_COMPLETE: 'quizball_training_complete',
  TRAINING_AUCTION_COMPLETE: 'quizball_training_auction_complete',
  TRAINING_GRID_COMPLETE: 'quizball_training_grid_complete',
  DEV_MODE: 'quizball_dev_mode',
} as const;

type StorageKey = typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS];

// Migration map: old key -> new key
const MIGRATIONS: Partial<Record<string, StorageKey>> = {
  'quizball-locale': STORAGE_KEYS.LOCALE,
  'onboardingCompleted': STORAGE_KEYS.ONBOARDING_COMPLETE,
  'walkthroughCompleted': STORAGE_KEYS.WALKTHROUGH_COMPLETE,
  'lastWheelSpin': STORAGE_KEYS.WHEEL_SPIN_TIMESTAMP,
};

// Run migrations once eagerly rather than on every get() call
let _migrated = false;
function runMigrations() {
  if (_migrated) return;
  try {
    for (const [legacyKey, newKey] of Object.entries(MIGRATIONS)) {
      if (!newKey) continue;
      const val = localStorage.getItem(legacyKey);
      if (val) {
        localStorage.setItem(newKey, val);
        localStorage.removeItem(legacyKey);
      }
    }
    _migrated = true;
  } catch {
    // Storage unavailable — will retry next call
  }
}

export const storage = {
  get<T>(key: StorageKey, defaultValue: T): T {
    try {
      runMigrations();
      const item = localStorage.getItem(key);
      if (!item) return defaultValue;
      try {
        return JSON.parse(item);
      } catch {
        return item as T; // Raw string fallback (e.g. legacy auth tokens)
      }
    } catch {
      return defaultValue;
    }
  },

  set<T>(key: StorageKey, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage full or unavailable
    }
  },

  remove(key: StorageKey): void {
    localStorage.removeItem(key);
  },

  clear(): void {
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
  },
};
